import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Presentation, Sparkles, Download, Loader2, ImagePlus, Trash2, X, Layers, RefreshCw, UploadCloud, ChevronLeft, ChevronRight, Eye, FileSpreadsheet } from "lucide-react";
import Sidebar from "../components/Sidebar";
import RenovacionWizard from "./presentaciones/RenovacionWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PresentacionesIAPage() {
  const [tab, setTab] = useState("ia");
  const [form, setForm] = useState({ cliente: "", titulo: "", anio: String(new Date().getFullYear()), instrucciones: "", textos: "", num_slides: "8", tipo_presentacion: "corporativa", generar_imagenes_ia: false });
  const [imagenes, setImagenes] = useState([]);
  const [logoCliente, setLogoCliente] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [dragOverLogo, setDragOverLogo] = useState(false);
  const [regenLoading, setRegenLoading] = useState(null);
  const [previews, setPreviews] = useState([]);
  const [previewIdx, setPreviewIdx] = useState(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const getAuthHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }), []);

  const fetchHistorial = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/presentaciones-ia/historial`, getAuthHeaders());
      setHistorial(res.data?.items || []);
    } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  useEffect(() => { fetchHistorial(); }, [fetchHistorial]);

  const addFiles = (fileList) => {
    const nuevas = Array.from(fileList || []).filter(f => f.type.startsWith("image/")).slice(0, 10 - imagenes.length);
    if (nuevas.length === 0) return;
    setImagenes(prev => [...prev, ...nuevas]);
  };

  const handleImagenes = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer?.files);
  };

  const handleRegenerarSlide = async (i, regenTexto, regenImagen) => {
    if (!resultado?.id || regenLoading) return;
    const key = `${i}-${regenImagen ? "img" : "txt"}`;
    try {
      setRegenLoading(key);
      const res = await axios.post(
        `${API}/presentaciones-ia/${resultado.id}/regenerar-slide`,
        { slide_index: i, regenerar_texto: regenTexto, regenerar_imagen: regenImagen, instrucciones: "" },
        getAuthHeaders()
      );
      setResultado(res.data);
      toast.success(regenImagen ? "Imagen regenerada e insertada en el PPTX" : "Diapositiva regenerada");
      fetchHistorial();
      loadPreviews(res.data.id, res.data.num_previews);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo regenerar la diapositiva");
    } finally {
      setRegenLoading(null);
    }
  };

  const loadPreviews = useCallback(async (id, count) => {
    setPreviews(prev => { prev.forEach(u => URL.revokeObjectURL(u)); return []; });
    if (!id || !count) return;
    try {
      const urls = await Promise.all(
        Array.from({ length: count }, (_, i) =>
          axios.get(`${API}/presentaciones-ia/${id}/preview/${i}`, { ...getAuthHeaders(), responseType: "blob" })
            .then(r => URL.createObjectURL(r.data))
        )
      );
      setPreviews(urls);
    } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  const handleGenerar = async () => {
    if (!form.cliente.trim() || !form.titulo.trim()) {
      toast.error("Indica el cliente y el título de la propuesta");
      return;
    }
    try {
      setGenerando(true);
      setResultado(null);
      const fd = new FormData();
      fd.append("cliente", form.cliente);
      fd.append("titulo", form.titulo);
      fd.append("anio", form.anio);
      fd.append("instrucciones", form.instrucciones);
      fd.append("textos", form.textos);
      fd.append("num_slides", form.num_slides);
      fd.append("tipo_presentacion", form.tipo_presentacion);
      fd.append("generar_imagenes_ia", form.generar_imagenes_ia ? "si" : "no");
      if (logoCliente) fd.append("logo_cliente", logoCliente);
      imagenes.forEach(img => fd.append("files", img));
      const res = await axios.post(`${API}/presentaciones-ia/generar`, fd, {
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setResultado(res.data);
      toast.success("¡Presentación generada!");
      fetchHistorial();
      loadPreviews(res.data.id, res.data.num_previews);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo generar la presentación");
    } finally {
      setGenerando(false);
    }
  };

  const handleDescargar = async (id, nombre) => {
    try {
      const res = await axios.get(`${API}/presentaciones-ia/${id}/descargar`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = nombre || "presentacion.pptx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el archivo");
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta presentación del historial?")) return;
    try {
      await axios.delete(`${API}/presentaciones-ia/${id}`, getAuthHeaders());
      toast.success("Presentación eliminada");
      if (resultado?.id === id) setResultado(null);
      fetchHistorial();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-6 pt-14 md:pt-6 space-y-6" data-testid="presentaciones-ia-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-green-700 text-white">
          <Presentation className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Presentaciones IA</h1>
          <p className="text-sm text-slate-500">Genera propuestas corporativas profesionales en segundos, con la plantilla oficial de Q Asesores</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setTab("ia")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "ia" ? "bg-emerald-600 text-white" : "bg-white border text-slate-600 hover:bg-slate-50"}`}
          data-testid="tab-propuestas-ia"
        >
          <Sparkles className="w-4 h-4" /> Propuestas IA
        </button>
        <button
          type="button"
          onClick={() => setTab("renovacion")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "renovacion" ? "bg-emerald-600 text-white" : "bg-white border text-slate-600 hover:bg-slate-50"}`}
          data-testid="tab-renovacion"
        >
          <FileSpreadsheet className="w-4 h-4" /> Renovación Corporativa
        </button>
      </div>

      {tab === "renovacion" && <RenovacionWizard />}

      {tab === "ia" && (
      <>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Formulario */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-600" /> Nueva presentación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="mb-2 block">¿Deseas una presentación corporativa?</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, tipo_presentacion: "corporativa" })}
                  className={`text-left border rounded-lg px-3 py-2.5 transition-colors ${form.tipo_presentacion === "corporativa" ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="ppt-tipo-corporativa"
                >
                  <p className="text-sm font-semibold text-slate-800">Sí, corporativa</p>
                  <p className="text-[11px] text-slate-500">Usa la plantilla oficial (intro de 6 diapositivas + año en portada)</p>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, tipo_presentacion: "tematica", generar_imagenes_ia: true })}
                  className={`text-left border rounded-lg px-3 py-2.5 transition-colors ${form.tipo_presentacion === "tematica" ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid="ppt-tipo-tematica"
                >
                  <p className="text-sm font-semibold text-slate-800">No, temática</p>
                  <p className="text-[11px] text-slate-500">Diseño acorde al tema con portada generada por IA, manteniendo colores y logo corporativos</p>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cliente *</Label>
                <Input value={form.cliente} onChange={e => setForm({ ...form, cliente: e.target.value })} placeholder="Ej: Grupo Corripio" data-testid="ppt-cliente-input" />
              </div>
              <div>
                <Label>Año (portada)</Label>
                <Input value={form.anio} onChange={e => setForm({ ...form, anio: e.target.value })} data-testid="ppt-anio-input" />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Logo del cliente (para la diapositiva de presentación)</Label>
              {!logoCliente ? (
                <div
                  onClick={() => logoInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOverLogo(true); }}
                  onDragLeave={() => setDragOverLogo(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOverLogo(false); const f = Array.from(e.dataTransfer?.files || []).find(x => x.type.startsWith("image/")); if (f) setLogoCliente(f); }}
                  className={`border-2 border-dashed rounded-lg px-4 py-4 text-center cursor-pointer transition-colors ${dragOverLogo ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-emerald-400 hover:bg-slate-50"}`}
                  data-testid="ppt-logo-dropzone"
                >
                  <UploadCloud className={`w-5 h-5 mx-auto mb-1 ${dragOverLogo ? "text-emerald-600" : "text-slate-400"}`} />
                  <p className="text-sm text-slate-600 font-medium">Arrastra y suelta el logo del cliente aquí</p>
                  <p className="text-[11px] text-slate-400">o haz click para seleccionarlo</p>
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f && f.type.startsWith("image/")) setLogoCliente(f); e.target.value = ""; }} className="hidden" data-testid="ppt-logo-cliente-input" />
                </div>
              ) : (
                <div className="flex items-center gap-3 border rounded-lg px-3 py-2 bg-slate-50">
                  <img src={URL.createObjectURL(logoCliente)} alt="Logo cliente" className="h-10 max-w-[140px] object-contain" />
                  <span className="text-xs text-slate-600 flex-1 truncate">{logoCliente.name}</span>
                  <button type="button" onClick={() => setLogoCliente(null)} className="text-red-500 hover:text-red-600 p-1" data-testid="ppt-logo-cliente-quitar">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <p className="text-[11px] text-slate-400 mt-1">Se colocará en la diapositiva "Presentación de Propuesta para:" en lugar del logo de ejemplo.</p>
            </div>
            <div>
              <Label>Título de la propuesta *</Label>
              <Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Pólizas institucionales" data-testid="ppt-titulo-input" />
            </div>
            <div>
              <Label>Tema / instrucciones para la IA</Label>
              <Textarea rows={2} value={form.instrucciones} onChange={e => setForm({ ...form, instrucciones: e.target.value })} placeholder="Ej: Propuesta de renovación de póliza de incendio, enfocar en ahorro y coberturas ampliadas" data-testid="ppt-instrucciones-input" />
            </div>
            <div>
              <Label>Textos que deben incluir las diapositivas</Label>
              <Textarea rows={6} value={form.textos} onChange={e => setForm({ ...form, textos: e.target.value })} placeholder="Pega aquí los textos, cifras, coberturas, primas, etc. La IA los organizará en diapositivas profesionales sin cambiar los datos." data-testid="ppt-textos-input" />
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <Label>Diapositivas de contenido</Label>
                <Select value={form.num_slides} onValueChange={v => setForm({ ...form, num_slides: v })}>
                  <SelectTrigger data-testid="ppt-numslides-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["5", "6", "8", "10", "12", "15"].map(n => <SelectItem key={n} value={n}>{n} diapositivas</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1 mb-1.5"><ImagePlus className="w-3.5 h-3.5" /> Imágenes (opcional, máx 10)</Label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg px-4 py-5 text-center cursor-pointer transition-colors ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-emerald-400 hover:bg-slate-50"}`}
                data-testid="ppt-dropzone"
              >
                <UploadCloud className={`w-6 h-6 mx-auto mb-1 ${dragOver ? "text-emerald-600" : "text-slate-400"}`} />
                <p className="text-sm text-slate-600 font-medium">Arrastra y suelta tus imágenes aquí</p>
                <p className="text-[11px] text-slate-400">o haz click para seleccionarlas ({imagenes.length}/10)</p>
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImagenes} className="hidden" data-testid="ppt-imagenes-input" />
              </div>
            </div>
            {imagenes.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {imagenes.map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={URL.createObjectURL(img)} alt="" className="w-16 h-16 object-cover rounded border" />
                    <button onClick={() => setImagenes(prev => prev.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="ppt-imagenes-ia-toggle">
              <input
                type="checkbox"
                checked={form.generar_imagenes_ia}
                onChange={e => setForm({ ...form, generar_imagenes_ia: e.target.checked })}
                className="accent-emerald-600 w-4 h-4"
              />
              <span className="text-sm text-slate-700">Generar imágenes con IA para las diapositivas (acordes al tema)</span>
            </label>
            <Button onClick={handleGenerar} disabled={generando} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="ppt-generar-btn">
              {generando ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando presentación... (puede tardar ~1 min)</> : <><Sparkles className="w-4 h-4" /> Generar presentación</>}
            </Button>
            <p className="text-[11px] text-slate-400">Las primeras 6 diapositivas son la introducción corporativa oficial (con el año actualizado en la portada); la IA genera el resto con el estilo de la marca.</p>
          </CardContent>
        </Card>

        {/* Resultado */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Layers className="w-4 h-4 text-emerald-600" /> Resultado</CardTitle>
          </CardHeader>
          <CardContent>
            {!resultado ? (
              <div className="text-center py-14 text-slate-400 text-sm">
                <Presentation className="w-12 h-12 mx-auto mb-3 opacity-30" />
                Genera una presentación para ver aquí su estructura
              </div>
            ) : (
              <div className="space-y-3" data-testid="ppt-resultado">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{resultado.titulo}</p>
                    <p className="text-xs text-slate-500">{resultado.cliente} · {resultado.anio} · {(resultado.tipo_presentacion === "tematica" ? 1 : 6) + (resultado.num_slides_generadas || 0)} diapositivas totales</p>
                  </div>
                  <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleDescargar(resultado.id, `Propuesta ${resultado.cliente} - ${resultado.anio}.pptx`)} data-testid="ppt-descargar-btn">
                    <Download className="w-4 h-4" /> Descargar PPTX
                  </Button>
                </div>
                <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
                  {previews.length > 0 && (
                    <div data-testid="ppt-previews">
                      <p className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1.5"><Eye className="w-3.5 h-3.5 text-emerald-600" /> Vista previa (click para ampliar)</p>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {previews.map((u, i) => (
                          <button key={i} type="button" onClick={() => setPreviewIdx(i)} className="shrink-0 border rounded-md overflow-hidden hover:ring-2 hover:ring-emerald-500 transition-shadow" data-testid={`ppt-preview-thumb-${i}`}>
                            <img src={u} alt={`Diapositiva ${i + 1}`} className="h-20 w-auto" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {resultado.tipo_presentacion === "tematica" ? (
                    <div className="border rounded-lg px-3 py-2 bg-slate-900 text-white text-xs">Portada temática generada por IA (logo y colores corporativos, año {resultado.anio})</div>
                  ) : (
                    <div className="border rounded-lg px-3 py-2 bg-slate-900 text-white text-xs">Diapositivas 1-6 · Introducción corporativa Q Asesores (año {resultado.anio})</div>
                  )}
                  {(resultado.slides || []).map((sl, i) => (
                    <div key={i} className={`border rounded-lg px-3 py-2 ${sl.tipo === "seccion" ? "bg-slate-800 text-white" : "bg-white"}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={sl.tipo === "seccion" ? "text-emerald-300 border-emerald-400" : "text-emerald-700 border-emerald-300"}>{i + (resultado.tipo_presentacion === "tematica" ? 2 : 7)}</Badge>
                        <p className="font-medium text-sm flex-1">{sl.titulo}</p>
                        <div className="flex gap-1 shrink-0">
                          <button
                            type="button"
                            title="Regenerar texto de esta diapositiva"
                            disabled={!!regenLoading}
                            onClick={() => handleRegenerarSlide(i, true, false)}
                            className={`p-1.5 rounded hover:bg-emerald-100 disabled:opacity-40 ${sl.tipo === "seccion" ? "text-emerald-300 hover:bg-slate-700" : "text-emerald-700"}`}
                            data-testid={`ppt-regen-texto-${i}`}
                          >
                            {regenLoading === `${i}-txt` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                          </button>
                          {sl.tipo !== "seccion" && (
                            <button
                              type="button"
                              title={sl.imagen != null ? "Regenerar la imagen de esta diapositiva" : "Generar una imagen IA para esta diapositiva"}
                              disabled={!!regenLoading}
                              onClick={() => handleRegenerarSlide(i, false, true)}
                              className="p-1.5 rounded text-emerald-700 hover:bg-emerald-100 disabled:opacity-40"
                              data-testid={`ppt-regen-imagen-${i}`}
                            >
                              {regenLoading === `${i}-img` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </div>
                      </div>
                      {sl.bullets?.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5 pl-8">
                          {sl.bullets.map((b, j) => <li key={j} className="text-xs text-slate-500 list-disc">{b}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Historial */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Historial de presentaciones</CardTitle>
        </CardHeader>
        <CardContent>
          {historial.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Aún no hay presentaciones generadas</p>
          ) : (
            <div className="divide-y">
              {historial.map(h => (
                <div key={h.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{h.titulo} <span className="text-slate-400">· {h.cliente}</span></p>
                    <p className="text-xs text-slate-400">{h.usuario_nombre} · {new Date(h.created_at).toLocaleString("es-DO")} · {6 + (h.num_slides_generadas || 0)} slides</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="gap-1 h-8" onClick={() => handleDescargar(h.id, `Propuesta ${h.cliente} - ${h.anio}.pptx`)} data-testid={`ppt-hist-descargar-${h.id}`}>
                      <Download className="w-3.5 h-3.5" /> PPTX
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-red-500 hover:text-red-600" onClick={() => handleEliminar(h.id)} data-testid={`ppt-hist-eliminar-${h.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de vista previa */}
      <Dialog open={previewIdx !== null} onOpenChange={(o) => { if (!o) setPreviewIdx(null); }}>
        <DialogContent className="max-w-5xl p-4" data-testid="ppt-preview-modal">
          <DialogTitle className="text-sm text-slate-700">
            Vista previa · Diapositiva {previewIdx !== null ? previewIdx + 1 : ""} de {previews.length}
          </DialogTitle>
          {previewIdx !== null && previews[previewIdx] && (
            <div className="relative">
              <img src={previews[previewIdx]} alt="Vista previa" className="w-full rounded-md border" />
              {previewIdx > 0 && (
                <button type="button" onClick={() => setPreviewIdx(previewIdx - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 border rounded-full p-1.5 shadow hover:bg-white" data-testid="ppt-preview-prev">
                  <ChevronLeft className="w-5 h-5 text-slate-700" />
                </button>
              )}
              {previewIdx < previews.length - 1 && (
                <button type="button" onClick={() => setPreviewIdx(previewIdx + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 border rounded-full p-1.5 shadow hover:bg-white" data-testid="ppt-preview-next">
                  <ChevronRight className="w-5 h-5 text-slate-700" />
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
      </main>
    </div>
  );
}
